import { useCallback, useMemo, useRef, useState } from "react";
import { validateEngineInput } from "../contracts/validate";
import type { EngineError } from "../contracts/types";
import type { GenerateMatrixResult, OperationMatrix } from "../domain/engine";
import { overrideCase as overrideCaseInMatrix } from "../domain/engine";
import type { FailureCase } from "../domain/failure-rules";
import { computeCoverage } from "../domain/coverage";
import { runEngine } from "../worker/workerClient";
import { FIXTURES, getFixtureById } from "../fixtures";
import type { EngineRunHandle } from "../worker/workerClient";
import { loadOverrides, saveOverrides } from "../storage/localStore";
import type { StoredOverrides } from "../storage/localStore";

export const MAX_INPUT_BYTES = 10 * 1024 * 1024;

export type InputState = "initial" | "edited" | "invalid" | "too-large";
export type RunState = "idle" | "processing" | "cancelled" | "completed" | "failed";

function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

function deriveInputState(text: string): { state: InputState; errors: EngineError[] } {
  if (byteLength(text) > MAX_INPUT_BYTES) {
    return {
      state: "too-large",
      errors: [{ code: "LIMIT_EXCEEDED", message: "La entrada supera el límite de 10 MB soportado en el navegador." }],
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    return {
      state: "invalid",
      errors: [
        {
          code: "INPUT_INVALID",
          message: `JSON inválido: ${error instanceof Error ? error.message : "no se pudo interpretar."}`,
        },
      ],
    };
  }

  const validation = validateEngineInput(parsed);
  if (!validation.valid) {
    return { state: "invalid", errors: validation.errors };
  }

  return { state: "edited", errors: [] };
}

export function useEngine() {
  const initialFixture = FIXTURES[0]!;
  const [scenarioId, setScenarioId] = useState(initialFixture.id);
  const [inputText, setInputText] = useState(() => JSON.stringify(initialFixture.input, null, 2));
  const [inputState, setInputState] = useState<InputState>("initial");
  const [inputErrors, setInputErrors] = useState<EngineError[]>([]);
  const [runState, setRunState] = useState<RunState>("idle");
  const [engineResult, setEngineResult] = useState<GenerateMatrixResult | null>(null);
  const handleRef = useRef<EngineRunHandle | null>(null);

  const selectScenario = useCallback((id: string) => {
    const fixture = getFixtureById(id);
    if (!fixture) return;
    const text = JSON.stringify(fixture.input, null, 2);
    const derived = deriveInputState(text);
    setScenarioId(id);
    setInputText(text);
    // Un fixture recién cargado que ya cumple el contrato se muestra como "initial"
    // (precargado); si el propio fixture es inválido (p. ej. el fixture "invalid-input"),
    // se señala de inmediato en vez de esperar a que el visitante lo edite.
    setInputState(derived.state === "edited" ? "initial" : derived.state);
    setInputErrors(derived.errors);
    setEngineResult(null);
    setRunState("idle");
  }, []);

  const editInput = useCallback((text: string) => {
    setInputText(text);
    setEngineResult(null);
    setRunState("idle");
    const derived = deriveInputState(text);
    setInputState(derived.state);
    setInputErrors(derived.errors);
  }, []);

  const run = useCallback(() => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(inputText);
    } catch {
      setInputState("invalid");
      return;
    }
    setRunState("processing");
    setEngineResult(null);
    const handle = runEngine(parsed);
    handleRef.current = handle;
    handle.result
      .then(async (result) => {
        let finalResult = result;
        try {
          const stored = await loadOverrides(result.output.evidence.scenarioId);
          if (stored) {
            let matrix = result.matrix;
            for (const [caseId, patch] of Object.entries(stored.overrides)) {
              matrix = overrideCaseInMatrix(matrix, caseId, patch).matrix;
            }
            const casesByOperation = new Map(matrix.map((operation) => [operation.operationId, operation.cases]));
            finalResult = { ...result, matrix, coverage: computeCoverage(casesByOperation) };
          }
        } catch {
          // IndexedDB no disponible o vacío: se continúa con el resultado sin overrides guardados.
        }
        setEngineResult(finalResult);
        setRunState(
          finalResult.output.status === "cancelled" ? "cancelled" : finalResult.output.status === "failed" ? "failed" : "completed",
        );
      })
      .catch((error: Error) => {
        setEngineResult(null);
        setRunState("failed");
        setInputErrors([{ code: "INTERNAL_ERROR", message: error.message }]);
      })
      .finally(() => {
        handleRef.current = null;
      });
  }, [inputText]);

  const cancel = useCallback(() => {
    handleRef.current?.cancel();
  }, []);

  const applyOverride = useCallback(
    (caseId: string, patch: Partial<Pick<FailureCase, "justification" | "accepted" | "reviewed" | "severity">>) => {
      setEngineResult((previous) => {
        if (!previous) return previous;
        const { matrix, coverage } = overrideCaseInMatrix(previous.matrix, caseId, patch);
        return { ...previous, matrix, coverage };
      });
    },
    [],
  );

  const matrix: OperationMatrix[] = useMemo(() => engineResult?.matrix ?? [], [engineResult]);

  const saveCurrentOverrides = useCallback(async (): Promise<void> => {
    if (!engineResult) return;
    const overrides: StoredOverrides["overrides"] = {};
    for (const operation of engineResult.matrix) {
      for (const failureCase of operation.cases) {
        overrides[failureCase.id] = {
          justification: failureCase.justification,
          accepted: failureCase.accepted,
          reviewed: failureCase.reviewed,
        };
      }
    }
    await saveOverrides({
      runId: engineResult.output.runId,
      scenarioId: engineResult.output.evidence.scenarioId,
      updatedAt: new Date().toISOString(),
      overrides,
    });
  }, [engineResult]);

  return {
    scenarioId,
    inputText,
    inputState,
    inputErrors,
    runState,
    engineResult,
    matrix,
    selectScenario,
    editInput,
    run,
    cancel,
    applyOverride,
    saveCurrentOverrides,
  };
}
